export const toShortDay = (str: string) => {
  switch (str) {
    case 'monday':
      return 'Mon';
    case 'tuesday':
      return 'Tue';
    case 'wednesday':
      return 'Wed';
    case 'thursday':
      return 'Thu';
    case 'friday':
      return 'Fri';
    case 'saturday':
      return 'Sat';
    case 'sunday':
      return 'Sun';
    default:
      return str;
  }
};

/** Today's calendar date in the given timezone, as YYYY-MM-DD. */
export const todayInTimezone = (timezone?: string | null): string => {
  const options: Intl.DateTimeFormatOptions = timezone
    ? { timeZone: timezone }
    : {};
  return new Intl.DateTimeFormat('en-CA', options).format(new Date());
};

/** Shifts a YYYY-MM-DD date by whole days, staying on the calendar. */
export const addDaysToDate = (date: string, days: number): string => {
  const shifted = new Date(`${date}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
};

const timezoneOffsetMs = (instant: Date, timezone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const part = (type: string) =>
    Number(parts.find(p => p.type === type)?.value);
  const asUtc = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second')
  );
  return asUtc - instant.getTime();
};

/** The last instant of a YYYY-MM-DD date in the given timezone, or in UTC without one. */
export const endOfDayInTimezone = (
  date: string,
  timezone?: string | null
): Date => {
  const asUtc = new Date(`${date}T23:59:59Z`);
  if (!timezone) {
    return asUtc;
  }
  return new Date(asUtc.getTime() - timezoneOffsetMs(asUtc, timezone));
};

/** Formats a YYYY-MM-DD date as e.g. "Wed, 12 Aug", free of the browser's timezone. */
export const formatShortDate = (date: string): string =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

export const getCurrencySymbol = (
  locale: string | undefined,
  currencyCode: string | undefined
): string => {
  if (!currencyCode) return '';
  const formattedLocale = locale ? locale.replace('_', '-') : 'en-GB';
  try {
    const parts = new Intl.NumberFormat(formattedLocale, {
      style: 'currency',
      currency: currencyCode,
    }).formatToParts(0);
    const symbolPart = parts.find(part => part.type === 'currency');
    return symbolPart ? symbolPart.value : currencyCode;
  } catch {
    return currencyCode;
  }
};

export const extractCheckId = (value: string | undefined): string => {
  if (!value) return '';
  const slashIndex = value.indexOf('/');
  if (slashIndex > 0) {
    return value.substring(0, slashIndex);
  }
  return value;
};

/**
 * Converts 24-hour time format (HH:MM) to 12-hour format (h:MM AM/PM)
 * @param time24 - Time string in 24-hour format (e.g., "16:46")
 * @returns Time string in 12-hour format (e.g., "4:46 PM")
 */
export const formatTime12Hour = (time24: string): string => {
  if (!time24) return time24;

  const [hours, minutes] = time24.split(':').map(num => parseInt(num, 10));

  if (isNaN(hours) || isNaN(minutes)) {
    return time24; // Return original if parsing fails
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/** Formats an order date (yyyy-MM-dd) and time (HH:mm) together, e.g. "Mon, 8 Sep, 12:02 PM". */
export const formatOrderDateTime = (
  dateStr: string | undefined,
  timeStr: string | undefined
): string => {
  if (!timeStr) return dateStr || '-';

  let displayDate: string;
  let displayTime: string;

  if (timeStr.includes('T')) {
    const parsed = new Date(timeStr);
    if (!isNaN(parsed.getTime())) {
      const isoDate = parsed.toISOString().slice(0, 10);
      displayDate = new Date(`${isoDate}T00:00:00Z`).toLocaleDateString(
        undefined,
        {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          timeZone: 'UTC',
        }
      );
      displayTime = formatTime12Hour(
        `${parsed.getUTCHours()}:${parsed.getUTCMinutes().toString().padStart(2, '0')}`
      );
    } else {
      return timeStr;
    }
  } else if (dateStr) {
    displayDate = new Date(`${dateStr}T00:00:00Z`).toLocaleDateString(
      undefined,
      {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }
    );
    displayTime = formatTime12Hour(timeStr);
  } else {
    return formatTime12Hour(timeStr);
  }

  return `${displayDate}, ${displayTime}`;
};
